import React from 'react';
import { TemplateRenderProps } from './types';
import { FormFields, AddonsSelector, CouponSection, TrustAndSecurityBadge, SocialProofAndFomo } from './CheckoutCommonElements';
import { BillingCycleSelector, PlanFeatureList, TestimonialCard, PriceBreakdown } from './CheckoutSummaryElements';

export const SplitDarkTemplate: React.FC<TemplateRenderProps> = ({
  plan,
  config,
  formData,
  onInputChange,
  billingCycle,
  onChangeCycle,
  selectedAddonIds,
  onToggleAddon,
  appliedCoupon,
  onApplyCoupon,
  onRemoveCoupon,
  onSubmitInfo,
  isCapturing,
  currencySymbol,
  addonsTotal,
  discountAmount,
  finalTotal,
}) => {
  return (
    <div
      className="min-h-full p-4 md:p-8 flex items-center justify-center bg-slate-900 text-slate-100"
      style={{
        fontFamily: config.fontFamily || 'Inter, sans-serif',
      }}
    >
      <div className="w-full max-w-4xl bg-slate-950 rounded-2xl border border-slate-800 shadow-2xl overflow-hidden grid grid-cols-1 md:grid-cols-12">
        {/* Left Dark Form */}
        <div className="md:col-span-7 p-6 md:p-8 space-y-5 bg-slate-900/60 border-b md:border-b-0 md:border-r border-slate-800">
          <div>
            <h1 className="text-xl font-black text-white">{config.headerMain || 'Your Account Details'}</h1>
            <p className="text-xs text-slate-400 mt-0.5">{config.headerSub || 'Setup will take less than 60 seconds.'}</p>
          </div>

          <SocialProofAndFomo config={config} />

          <form onSubmit={onSubmitInfo} className="space-y-4">
            <FormFields
              config={config}
              formData={formData}
              onInputChange={onInputChange}
              inputClassName="bg-slate-950 border-slate-800 text-white focus:border-indigo-500 placeholder:text-slate-500"
              labelClassName="text-slate-300 font-bold text-xs"
            />
            <AddonsSelector addons={config.addons} selectedAddonIds={selectedAddonIds} onToggleAddon={onToggleAddon} currency={plan.currency} />
            <CouponSection config={config} appliedCoupon={appliedCoupon} onApplyCoupon={onApplyCoupon} onRemoveCoupon={onRemoveCoupon} />

            <button
              type="submit"
              disabled={isCapturing}
              className="w-full py-3.5 text-xs font-black uppercase tracking-wider text-white shadow-xl transition-all hover:brightness-110 disabled:opacity-50"
              style={{
                backgroundColor: config.primaryColor || '#6366f1',
                borderRadius: config.buttonRadius || '8px',
              }}
            >
              {isCapturing ? 'Verifying...' : config.buttonText || 'Authorize Subscription'}
            </button>
          </form>

          <TrustAndSecurityBadge config={config} />
        </div>

        {/* Right Summary Column */}
        <div className="md:col-span-5 p-6 md:p-8 bg-slate-950 flex flex-col justify-between space-y-6">
          <div className="space-y-4">
            {config.logoUrl && <img src={config.logoUrl} alt="Logo" className="h-6 brightness-0 invert object-contain" />}
            <div>
              <p className="text-[10px] uppercase font-bold text-indigo-400 tracking-wider">Review Order</p>
              <h2 className="text-2xl font-black text-white">{plan.tier}</h2>
              {plan.description && <p className="text-xs text-slate-400 mt-1">{plan.description}</p>}
            </div>

            <BillingCycleSelector billingCycle={billingCycle} onChangeCycle={onChangeCycle} config={config} />
            <PlanFeatureList features={plan.features} textColorClass="text-slate-300" checkColorClass="text-indigo-400" />
            {config.showTestimonial && <TestimonialCard testimonial={config.testimonial} />}
          </div>

          <PriceBreakdown plan={plan} billingCycle={billingCycle} addonsTotal={addonsTotal} discountAmount={discountAmount} finalTotal={finalTotal} currencySymbol={currencySymbol} />
        </div>
      </div>
    </div>
  );
};
